import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EVENT_TYPES } from "@/lib/eventTypes";
import { euros } from "@/lib/weddingApi";
import { Upload, ClipboardPaste, AlertTriangle, FileText, Link2Off, Check } from "lucide-react";

const toEuros = (c) => ((c || 0) / 100).toFixed(2);
const toCents = (v) => Math.round(parseFloat(String(v).replace(",", ".") || 0) * 100);

export default function WeddingEmailImport() {
  const nav = useNavigate();
  const [gmail, setGmail] = useState(null);
  const [raw, setRaw] = useState("");
  const [imp, setImp] = useState(null);
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const loadHistory = () => api.get("/admin/wedding-imports").then(({ data }) => setHistory(data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/gmail/status").then(({ data }) => setGmail(data)).catch(() => {});
    loadHistory();
  }, []);

  const prefill = (doc) => {
    const f = doc.extraction?.fields || {};
    const val = (k, d = "") => (f[k]?.value ?? d);
    setForm({
      event_type: doc.extraction?.event_type_suggestion?.value || "",
      event_type_other: "",
      event_title: doc.extraction?.event_title_suggestion || "",
      organizer_name: val("organizer_name"),
      contact_email: val("contact_email"),
      contact_phone: "",
      event_date: val("event_date"),
      arrival_time: "",
      start_time: val("start_time"),
      end_time: val("end_time"),
      adult_price: toEuros(val("adult_price_cents", 10500)),
      child_price: toEuros(val("child_price_cents", 3500)),
      cocktail_price: toEuros(val("cocktail_price_cents", 3500)),
      cheese_price: toEuros(val("cheese_price_cents", 1000)),
      adults_count: val("adults_count", 0),
      children_count: val("children_count", 0),
      cocktail_participants: 0,
      cheese_participants: 0,
      acceptance_proof: doc.extraction?.acceptance?.excerpt || "",
      acceptance_confirmed: false,
      internal_notes: "",
    });
  };

  const analysePaste = async () => {
    setError(""); setMsg("");
    try {
      const { data } = await api.post("/admin/wedding-imports", { source: "paste", raw_text: raw });
      setImp(data);
      prefill(data);
      loadHistory();
      if (data.wedding_id) setMsg("Ce message a déjà été importé : le dossier existe.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const analyseEml = async (file) => {
    setError(""); setMsg("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/wedding-imports/eml", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImp(data);
      prefill(data);
      loadHistory();
      if (data.wedding_id) setMsg("Ce message a déjà été importé : le dossier existe.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const confirm = async () => {
    setError("");
    try {
      const { data } = await api.post(`/admin/wedding-imports/${imp.id}/confirm`, {
        event_type: form.event_type,
        event_type_other: form.event_type_other || "",
        event_title: form.event_title,
        organizer_name: form.organizer_name || "",
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        event_date: form.event_date,
        arrival_time: form.arrival_time,
        start_time: form.start_time,
        end_time: form.end_time,
        acceptance_proof: form.acceptance_proof,
        acceptance_confirmed: form.acceptance_confirmed,
        internal_notes: form.internal_notes,
        contract: {
          adult_price_cents: toCents(form.adult_price),
          child_price_cents: toCents(form.child_price),
          cocktail_price_cents: toCents(form.cocktail_price),
          cheese_price_cents: toCents(form.cheese_price),
          adults_count: Number(form.adults_count) || 0,
          children_count: Number(form.children_count) || 0,
          cocktail_participants: Number(form.cocktail_participants) || 0,
          cheese_participants: Number(form.cheese_participants) || 0,
        },
      });
      nav(`/admin/receptions/${data.id}`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const ex = imp?.extraction;

  return (
    <div data-testid="wedding-import-page">
      <p className="overline">Préparer un dossier</p>
      <h1 className="font-display text-4xl mt-3 font-light">Emails & imports</h1>
      <p className="mt-3 text-sm text-[#697585]">
        Tous types de réceptions : mariage, baptême, anniversaire, réception après obsèques, etc.
        Le type est suggéré puis confirmé par vos soins.
      </p>

      <section className="mt-8 bg-white border border-[#D9D3C5] p-6">
        <h2 className="font-display text-xl font-light flex items-center gap-2"><Link2Off size={16} /> Boîte Gmail</h2>
        <p data-testid="gmail-status" className="mt-2 text-sm text-[#697585]">
          {gmail?.connected
            ? `Compte connecté : ${gmail.account}`
            : `Non connectée. ${gmail?.reason || "Identifiants OAuth Google requis."}`}
        </p>
      </section>

      <section className="mt-6 bg-white border border-[#D9D3C5] p-6">
        <h2 className="font-display text-xl font-light flex items-center gap-2"><ClipboardPaste size={16} /> Coller un email</h2>
        <textarea
          data-testid="import-paste-textarea"
          className="field w-full min-h-[200px] mt-4"
          placeholder={"Collez ici la proposition ET la réponse du client (en-têtes De: / Objet: inclus si possible)"}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
        />
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <button data-testid="import-analyse-btn" className="btn-primary" onClick={analysePaste}>Analyser</button>
          <label data-testid="import-eml-label" className="btn-ghost inline-flex items-center gap-2 cursor-pointer">
            <Upload size={14} /> Importer un .eml
            <input
              data-testid="import-eml-input"
              type="file"
              accept=".eml,message/rfc822"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && analyseEml(e.target.files[0])}
            />
          </label>
          <span className="text-xs text-[#697585]">L'analyse n'envoie aucun email et ne crée rien tant que vous ne confirmez pas.</span>
        </div>
        {error && <p data-testid="import-error" className="mt-4 text-sm text-red-600">{error}</p>}
        {msg && <p data-testid="import-msg" className="mt-4 text-sm text-[#B8956A]">{msg}</p>}
      </section>

      {ex && (
        <section className="mt-6 bg-white border border-[#D9D3C5] p-6">
          <h2 className="font-display text-xl font-light flex items-center gap-2"><FileText size={16} /> Extraction</h2>
          <p className="mt-2 text-xs text-[#697585]">
            De : {imp.headers?.from || "—"} · Objet : {imp.headers?.subject || "—"} · {imp.headers?.date || ""}
          </p>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(ex.fields || {}).map(([k, v]) => (
              <div key={k} data-testid={`extract-${k}`} className="border border-[#D9D3C5] p-4">
                <p className="overline">{k}</p>
                <p className="mt-1 text-sm font-medium">
                  {k.endsWith("_cents") ? euros(v.value) : String(v.value)}
                </p>
                <p className="mt-2 text-xs text-[#697585]">Source : {v.source}</p>
                {v.excerpt && <p className="mt-1 text-xs italic text-[#697585]">« {v.excerpt} »</p>}
              </div>
            ))}
          </div>

          <div className="mt-6 space-y-2">
            <p className="overline">Type d'événement suggéré</p>
            <p data-testid="extract-type-suggestion" className="text-sm">
              {ex.event_type_suggestion?.value
                ? `${EVENT_TYPES.find((t) => t.value === ex.event_type_suggestion.value)?.label || ex.event_type_suggestion.value}`
                : "Non identifié — à choisir ci-dessous."}
              {ex.event_type_suggestion?.excerpt && (
                <span className="text-[#697585] italic"> · « {ex.event_type_suggestion.excerpt} »</span>
              )}
            </p>
            {ex.event_type_suggestion?.note && (
              <p className="text-sm text-[#B8956A]">{ex.event_type_suggestion.note}</p>
            )}
          </div>

          <div className="mt-6 space-y-2">
            <p className="overline">Preuve d'acceptation</p>
            {ex.acceptance?.detected ? (
              <p data-testid="extract-acceptance" className="text-sm text-emerald-700 flex gap-2">
                <Check size={16} className="mt-0.5" /> Formule d'accord détectée : « {ex.acceptance.excerpt} »
              </p>
            ) : (
              <p data-testid="extract-acceptance" className="text-sm text-[#B8956A] flex gap-2">
                <AlertTriangle size={16} className="mt-0.5" />
                {ex.acceptance?.ambiguous
                  ? `Message ambigu : « ${ex.acceptance.excerpt} » — un remerciement n'est pas une acceptation.`
                  : "Aucune acceptation explicite trouvée."}
              </p>
            )}
            <p className="text-xs text-[#697585]">Aucun acompte n'est jamais déduit d'un email.</p>
          </div>

          {(!!ex.uncertainties?.length || !!ex.contradictions?.length) && (
            <div className="mt-6 border-l-2 border-[#B8956A] pl-4">
              <p className="overline">À vérifier</p>
              <ul className="mt-2 space-y-1">
                {[...(ex.contradictions || []), ...(ex.uncertainties || [])].map((u, i) => (
                  <li key={i} data-testid={`extract-uncertainty-${i}`} className="text-sm">· {u}</li>
                ))}
              </ul>
            </div>
          )}

          {!!(ex.attachments || []).length && (
            <div className="mt-6">
              <p className="overline">Pièces jointes détectées</p>
              <ul className="mt-2 text-sm space-y-1">
                {ex.attachments.map((a, i) => (
                  <li key={i}>· {a.filename} — {a.content_type} — {Math.round((a.size || 0) / 1024)} Ko</li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-[#697585]">
                Les pièces jointes ne sont pas publiées : la gestion des vidéos privées arrive avec la connexion Gmail.
              </p>
            </div>
          )}
        </section>
      )}

      {form && (
        <section className="mt-6 bg-white border border-[#D9D3C5] p-6">
          <h2 className="font-display text-xl font-light">Vérifier et créer le dossier</h2>
          <p className="mt-2 text-sm text-[#697585]">
            Les tarifs de l'email accepté priment sur les textes du site. Vérifiez chaque champ incertain.
          </p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block sm:col-span-2">
              <span className="text-xs uppercase tracking-[0.14em]">Type d'événement *</span>
              <select data-testid="confirm-event-type" className="field w-full mt-2" value={form.event_type}
                      onChange={(e) => setForm({ ...form, event_type: e.target.value })}>
                <option value="">— Choisir un type —</option>
                {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            {form.event_type === "other" && (
              <input data-testid="confirm-event-type-other" className="field sm:col-span-2" placeholder="Précisez le type *"
                     value={form.event_type_other} onChange={(e) => setForm({ ...form, event_type_other: e.target.value })} />
            )}
            <input data-testid="confirm-title" className="field sm:col-span-2" placeholder="Titre de la réception *"
                   value={form.event_title} onChange={(e) => setForm({ ...form, event_title: e.target.value })} />
            <input data-testid="confirm-organizer" className="field" placeholder="Nom de l'organisateur"
                   value={form.organizer_name} onChange={(e) => setForm({ ...form, organizer_name: e.target.value })} />
            <input data-testid="confirm-email" className="field" placeholder="Email du client *" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            <input data-testid="confirm-phone" className="field" placeholder="Téléphone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
            <input data-testid="confirm-date" type="date" className="field" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
            <input className="field" placeholder="Arrivée" value={form.arrival_time} onChange={(e) => setForm({ ...form, arrival_time: e.target.value })} />
            <input className="field" placeholder="Début" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
            <input className="field" placeholder="Fin" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            <input data-testid="confirm-adult-price" className="field" placeholder="Prix adulte (€)" value={form.adult_price} onChange={(e) => setForm({ ...form, adult_price: e.target.value })} />
            <input data-testid="confirm-child-price" className="field" placeholder="Prix enfant (€)" value={form.child_price} onChange={(e) => setForm({ ...form, child_price: e.target.value })} />
            <input className="field" placeholder="Prix cocktail (€)" value={form.cocktail_price} onChange={(e) => setForm({ ...form, cocktail_price: e.target.value })} />
            <input className="field" placeholder="Prix fromage (€)" value={form.cheese_price} onChange={(e) => setForm({ ...form, cheese_price: e.target.value })} />
            <input data-testid="confirm-adults" className="field" placeholder="Adultes" value={form.adults_count} onChange={(e) => setForm({ ...form, adults_count: e.target.value })} />
            <input className="field" placeholder="Enfants" value={form.children_count} onChange={(e) => setForm({ ...form, children_count: e.target.value })} />
            <input className="field" placeholder="Participants cocktail" value={form.cocktail_participants} onChange={(e) => setForm({ ...form, cocktail_participants: e.target.value })} />
            <input className="field" placeholder="Participants fromage" value={form.cheese_participants} onChange={(e) => setForm({ ...form, cheese_participants: e.target.value })} />
          </div>
          <textarea data-testid="confirm-proof" className="field w-full min-h-[90px] mt-3" placeholder="Preuve d'acceptation (extrait)" value={form.acceptance_proof} onChange={(e) => setForm({ ...form, acceptance_proof: e.target.value })} />
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="confirm-acceptance" checked={form.acceptance_confirmed} onChange={(e) => setForm({ ...form, acceptance_confirmed: e.target.checked })} />
            Je confirme que cette proposition a été acceptée (n'implique aucun acompte reçu)
          </label>
          <button data-testid="confirm-create-btn" className="btn-primary mt-5" onClick={confirm}>Créer le dossier</button>
        </section>
      )}

      <section className="mt-10">
        <p className="overline">Imports récents</p>
        <ul className="mt-3 space-y-2">
          {history.map((h) => (
            <li key={h.id} data-testid={`import-history-${h.id}`} className="bg-white border border-[#D9D3C5] p-4 text-sm flex flex-wrap items-center justify-between gap-3">
              <span>
                {h.headers?.subject || "(sans objet)"} · <span className="text-[#697585]">{h.source}</span>
              </span>
              <span className="text-xs uppercase tracking-widest text-[#B8956A]">
                {h.wedding_id ? "dossier créé" : h.state}
              </span>
            </li>
          ))}
          {!history.length && <li className="text-sm text-[#697585]">Aucun import.</li>}
        </ul>
      </section>
    </div>
  );
}
